import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Coins, Loader2, Zap, Star, Crown, Gem, Gift } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price: number;
  priceId: string;
  icon: React.ReactNode;
  popular?: boolean;
}

const BASE_RATE = 2.99 / 50; // Starter = baseline price per credit

const packs: CreditPack[] = [
  { id: 'starter', name: 'Starter', credits: 50, price: 2.99, priceId: 'price_1Ta0BJDdjifmi34XjoN5UGFN', icon: <Coins className="w-5 h-5" /> },
  { id: 'growth', name: 'Growth', credits: 200, price: 9.99, priceId: 'price_1Ta0BKDdjifmi34XdtMzOxGG', icon: <Zap className="w-5 h-5" />, popular: true },
  { id: 'pro', name: 'Pro', credits: 500, price: 22.99, priceId: 'price_1Ta0BLDdjifmi34Xzgzf33nX', icon: <Star className="w-5 h-5" /> },
  { id: 'elite', name: 'Elite', credits: 2000, price: 69.99, priceId: 'price_1Ta0BMDdjifmi34XvHRGEaIb', icon: <Crown className="w-5 h-5" /> },
  { id: 'ultimate', name: 'Ultimate', credits: 35000, price: 499, priceId: 'price_1Ta0BMDdjifmi34XHEEDhmqp', icon: <Gem className="w-5 h-5" /> },
];

function getSavingPercent(pack: CreditPack): number {
  const perCredit = pack.price / pack.credits;
  const saving = ((BASE_RATE - perCredit) / BASE_RATE) * 100;
  return Math.round(saving);
}

interface BuyCreditsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BuyCreditsDialog = ({ open, onOpenChange }: BuyCreditsDialogProps) => {
  const [selectedPack, setSelectedPack] = useState<string>('growth');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleBuy = async () => {
    const pack = packs.find(p => p.id === selectedPack);
    if (!pack) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Please log in first.');
        setLoading(false);
        return;
      }
      const { data, error } = await supabase.functions.invoke('create-credit-checkout', {
        body: { pack: pack.id },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err: any) {
      console.error('Credit checkout error:', err);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Gem className="w-5 h-5 text-primary" />
            Buy AI Credits
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground mb-4">
          Credits never expire. Use them across all AI tools.
        </p>

        <div className="grid gap-3">
          {packs.map((pack) => {
            const saving = getSavingPercent(pack);
            const isSelected = selectedPack === pack.id;
            return (
              <button
                key={pack.id}
                onClick={() => setSelectedPack(pack.id)}
                className={`relative flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left w-full ${
                  isSelected
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                    : pack.popular
                      ? 'border-primary/30 bg-primary/5 hover:border-primary/50'
                      : 'border-border bg-card hover:border-primary/30'
                }`}
              >
                {pack.popular && (
                  <span className="absolute -top-2 right-3 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
                    Popular
                  </span>
                )}
                <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary/15 text-primary' : pack.popular ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                  {pack.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-foreground">{pack.name}</p>
                    {saving > 0 && (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                        Save {saving}%
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{pack.credits} credits</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-base font-bold text-foreground">${pack.price}</p>
                  <p className="text-[10px] text-muted-foreground">
                    ${(pack.price / pack.credits).toFixed(3)}/cr
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        <Button
          onClick={handleBuy}
          disabled={loading}
          className="w-full mt-4"
          size="lg"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Coins className="w-4 h-4 mr-2" />
          )}
          Pay ${packs.find(p => p.id === selectedPack)?.price ?? ''}
        </Button>
        {/* Referral CTA */}
        <div className="mt-3 pt-3 border-t border-border text-center">
          <button
            onClick={() => { onOpenChange(false); navigate('/affiliates'); }}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
          >
            <Gift className="w-3.5 h-3.5" />
            Or <span className="font-semibold underline underline-offset-2">refer friends</span> to earn credits for free
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BuyCreditsDialog;
